import React, { useState } from 'react';
import { Lock, Trash2, ArrowRight, MessageSquare } from 'lucide-react';
import { formatRelativeTime, truncateText } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

/**
 * Chat Card Component
 * 
 * Displays calculation summary in a card format
 */
const ChatCard = ({
  chat,
  onOpen,
  onDelete,
  className = '',
}) => {
  const { user } = useAuth();
  const { getMaterialIcon } = useChat();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(chat.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting chat:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const messageCount = chat.messages?.length || 0;

  return (
    <>
      <div className={`bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow ${className}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Header with icon and type */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{getMaterialIcon(chat.material_type)}</span>
              <span className="font-semibold text-gray-800 capitalize">
                {chat.material_type}
              </span>
            </div>
            
            {/* First message */}
            <div className="text-gray-600 mb-2">
              "{truncateText(chat.first_message, 80)}"
            </div>
            
            {/* Result summary */}
            {chat.result_summary && (
              <div className="text-sm text-gray-500 mb-3">
                {truncateText(chat.result_summary, 100)}
              </div>
            )}
            
            {/* Meta info */}
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span>{formatRelativeTime(chat.created_at)}</span>
              <span className="flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                {messageCount} {messageCount === 1 ? 'message' : 'messages'}
              </span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => onOpen(chat)}
              className={`
                px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors
                ${!user
                  ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                }
              `}
            >
              {!user ? (
                <>
                  <Lock className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign in</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Open</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            
            {user && onDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Calculation?"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this calculation? This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button 
            variant="ghost" 
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            loading={isDeleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ChatCard;
